import type {StyleProp, ViewStyle} from 'react-native';

import type {CSSObject} from 'styled-components';

import Color from 'color';
import styled, {css} from 'styled-components';

import type {DOMStyleObject} from '../../utils/utility-types';
import type {StyledProps} from './types';

import SearchSvg from '../../assets/icons/search.svg';
import CloseSvg from '../../assets/icons/secondary/Close.svg';
import {getColor, getFontWeight, ms, square} from '../../styles';
import {platform} from '../../utils/platform';
import {Clickable} from '../Clickable';
import {Text} from '../Text';
import {View} from '../View';

export const Container = styled(View)`
  position: relative;
`;

export const textBase = css<StyledProps>`
  font-size: ${ms(14)};
  color: ${getColor('text1')};

  ${p =>
    p.disabled &&
    css`
      color: ${Color(getColor('text3')(p)).alpha(0.6).toString()};
    `}
`;

const base = css<StyledProps>`
  ${textBase};
  position: relative;
  border: 1px solid ${getColor('border2')};
  border-radius: ${ms(8)};
  outline: none;
  width: 100%;

  /* it makes the input looks identical both on iOS and Android */
  height: ${ms(47)};
  padding: ${ms(15)} ${ms(16)};
  background-color: ${p =>
    p.theme.id === 'light' ? getColor('background1') : getColor('background2')};
  caret-color: ${getColor('global1')};

  ${platform.isWeb &&
  css<StyledProps>`
    &::placeholder {
      color: ${p => p.placeholderColor || getColor('text3')};
      font-weight: ${getFontWeight('regular')};
    }

    /* disable touch callout */
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0);
  `}

  ${p =>
    p.disabled &&
    css`
      background-color: rgba(0, 0, 0, 0);
      cursor: not-allowed;

      &::placeholder {
        color: ${Color(p.placeholderColor || getColor('text3')(p))
          .alpha(0.6)
          .toString()};
      }
    `}

  ${p =>
    p.error &&
    css`
      border-color: ${getColor('error')};
    `}

  ${p =>
    p.appearance === 'search' &&
    css`
      padding-left: ${ms(16 + 24)};
    `}

    ${p =>
    p.isClearable &&
    css`
      padding-right: ${ms(16 + 24)};
    `}
`;

export const ErrorText = styled(Text)<{
  disabled?: boolean;
  errorStyle?: DOMStyleObject | StyleProp<ViewStyle>;
}>`
  color: ${getColor('error')};
  font-size: ${ms(11)};
  line-height: ${ms(11)};
  text-align: left;
  position: absolute;
  left: ${ms(16)};
  right: ${ms(16)};
  top: ${ms(platform.isWeb ? 2 : 3)};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  background-color: ${p =>
    p.theme.id === 'light' ? getColor('background1') : getColor('background2')};

  ${p =>
    p.disabled &&
    css`
      background-color: rgba(0, 0, 0, 0);
    `}

  ${p => p.errorStyle as CSSObject};
`;

const SSearchIcon = styled(SearchSvg).attrs(p => ({
  fill: getColor('icon3')(p),
}))`
  width: ${ms(24)};
  height: ${ms(24)};
  position: absolute;
  left: ${ms(14)};
  top: ${ms(12)};
`;

export {SSearchIcon as SearchIcon};

export const ClearButton = styled(Clickable)`
  position: absolute;
  inset-inline-end: ${ms(10)};
  inset-block-start: ${ms(12)};
`;

export const ClearIcon = styled(CloseSvg).attrs(p => ({
  fill: getColor('error')(p),
}))`
  ${square(22)};
`;

export const web = css<StyledProps>`
  box-sizing: border-box;
  ${base}
`;

export const native = css<StyledProps>`
  ${base}
`;
